import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { EstimateWithId } from '../types/estimation';
import { migrationService } from '../services/estimationMigrationService';
import { versionDetector } from '../services/estimationVersionDetector';
import { legacyAdapter } from '../services/legacyEstimationAdapter';
import { calculationService } from '../services/calculationService';
import { estimationDataService } from '../services/estimationDataService';
import { DetailedEstimationData, CategoryDiscount } from '../types/estimation-items';
import { formatPriceForPDF } from './pdfFormatter';

// ─── Constants ─────────────────────────────────────────────────────────────────

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  house: 'Maison',
  apartment: 'Appartement',
  building: 'Immeuble',
};

const C = {
  violet:       [61, 26, 120]   as [number, number, number], // #3D1A78
  orange:       [194, 81, 31]   as [number, number, number], // #C2511F
  orangeDot:    [249, 115, 22]  as [number, number, number], // #F97316
  white:        [255, 255, 255] as [number, number, number],
  darkText:     [30, 17, 72]    as [number, number, number], // #1E1148
  purple:       [76, 29, 149]   as [number, number, number], // #4C1D95
  subText:      [124, 111, 160] as [number, number, number], // #7C6FA0
  purpleBg:     [238, 234, 246] as [number, number, number], // #EEEAF6
  purplePale:   [237, 233, 254] as [number, number, number], // #EDE9FE
  purpleBorder: [196, 181, 253] as [number, number, number], // #C4B5FD
  headBg:       [245, 243, 255] as [number, number, number], // #F5F3FF
  rowAlt:       [250, 250, 250] as [number, number, number], // #FAFAFA
  subDetailBg:  [252, 252, 252] as [number, number, number],
  gray:         [156, 163, 175] as [number, number, number], // #9CA3AF
  mutedWhite:   [210, 200, 235] as [number, number, number], // white ~0.7 opacity on violet
  discountRed:  [180, 0, 0]     as [number, number, number],
  discountBg:   [254, 242, 242] as [number, number, number],
  separatorLine:[100, 80, 180]  as [number, number, number],
  separatorGray:[229, 231, 235] as [number, number, number], // #E5E7EB
};

const MARGIN        = 15;
const PAGE_W        = 210;
const PAGE_H        = 297;
const CONTENT_W     = PAGE_W - MARGIN * 2; // 180mm
const FOOTER_SEPARATOR_Y = 282;            // 15mm from bottom (#E5E7EB line)
const FOOTER_Y      = 288;                 // footer text baseline
const CONTENT_BOTTOM = FOOTER_SEPARATOR_Y - 5; // 277mm — safe content area
const HEADER_H      = 22;
const ROW_H        = 6;
const CAT_H        = 7;

// Column widths (total = 180mm)
const COL_W = [81, 14, 13, 22, 14, 18, 18] as const; // designation, qty, unit, puHT, tva, totalHT, totalTTC
const COL_HEADERS = ['Désignation', 'Qté', 'Unité', 'PU HT', 'TVA', 'Total HT', 'Total TTC'];
const COL_ALIGN: Array<'left' | 'center' | 'right'> = ['left', 'center', 'center', 'right', 'center', 'right', 'right'];

function colX(i: number): number {
  let x = MARGIN;
  for (let j = 0; j < i; j++) x += COL_W[j];
  return x;
}

// ─── Font loading ──────────────────────────────────────────────────────────────

let _robotoCache: { regular: string; medium: string } | null = null;

async function loadRoboto(doc: jsPDF): Promise<boolean> {
  try {
    if (!_robotoCache) {
      const toB64 = (buf: ArrayBuffer): string => {
        const bytes = new Uint8Array(buf);
        let bin = '';
        const chunk = 8192;
        for (let i = 0; i < bytes.length; i += chunk) {
          bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
        }
        return btoa(bin);
      };

      const [r1, r2] = await Promise.all([
        fetch('/fonts/Roboto-Regular.ttf'),
        fetch('/fonts/Roboto-Medium.ttf'),
      ]);

      if (!r1.ok || !r2.ok) throw new Error('Font fetch failed');

      const [b1, b2] = await Promise.all([r1.arrayBuffer(), r2.arrayBuffer()]);
      _robotoCache = { regular: toB64(b1), medium: toB64(b2) };
    }

    doc.addFileToVFS('Roboto-Regular.ttf', _robotoCache.regular);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFileToVFS('Roboto-Medium.ttf', _robotoCache.medium);
    doc.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');
    doc.setFont('Roboto', 'normal');
    return true;
  } catch (err) {
    console.warn('[PDF] Roboto unavailable, falling back to helvetica:', err);
    return false;
  }
}

// ─── Drawing helpers ───────────────────────────────────────────────────────────

function font(doc: jsPDF, roboto: boolean, style: 'normal' | 'bold' = 'normal') {
  doc.setFont(roboto ? 'Roboto' : 'helvetica', style);
}

function drawGradientHeader(doc: jsPDF) {
  const strips = 60;
  const sw = PAGE_W / strips;
  const [sr, sg, sb] = C.violet;
  const [er, eg, eb] = C.orange;
  for (let i = 0; i < strips; i++) {
    const t = i / (strips - 1);
    doc.setFillColor(
      Math.round(sr + t * (er - sr)),
      Math.round(sg + t * (eg - sg)),
      Math.round(sb + t * (eb - sb)),
    );
    doc.rect(i * sw, 0, sw + 0.5, HEADER_H, 'F');
  }
}

function drawFooter(doc: jsPDF, pageNum: number, total: number, roboto: boolean) {
  // Separator line at 15mm from bottom (#E5E7EB)
  doc.setDrawColor(...C.separatorGray);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, FOOTER_SEPARATOR_Y, PAGE_W - MARGIN, FOOTER_SEPARATOR_Y);
  // Footer text pinned to bottom
  font(doc, roboto, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.subText);
  doc.text('SimulReno — simulreno.fr', MARGIN, FOOTER_Y);
  doc.text(`Page ${pageNum} / ${total}`, PAGE_W - MARGIN, FOOTER_Y, { align: 'right' });
}

/** Returns y (possibly reset to top of a new page) */
function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > CONTENT_BOTTOM) {
    doc.addPage();
    return 20;
  }
  return y;
}

function truncate(doc: jsPDF, text: string, maxW: number): string {
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 0 && doc.getTextWidth(t + '…') > maxW) t = t.slice(0, -1);
  return t + '…';
}

// ─── Detailed PDF ──────────────────────────────────────────────────────────────

export async function generateDetailedPDF(
  data: DetailedEstimationData,
  discountsOverride?: CategoryDiscount[],
): Promise<void> {
  const doc = new jsPDF();
  const roboto = await loadRoboto(doc);

  // Adapt legacy format if needed
  let processedData = data;
  if (versionDetector.isLegacyFormat(data)) {
    processedData = legacyAdapter.adaptLegacyEstimation(data);
  }

  const deptCode = processedData.description?.location?.postcode?.substring(0, 2);
  const discounts: CategoryDiscount[] =
    discountsOverride ?? (processedData as any).discounts ?? (data as any).discounts ?? [];
  const result = calculationService.calculateEstimate(processedData, deptCode, discounts);

  const devisNum = `${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

  // ── HEADER ─────────────────────────────────────────────────────────────────
  drawGradientHeader(doc);

  // "SimulReno" in Roboto Medium 18pt white
  font(doc, roboto, 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text('SimulReno', MARGIN, 14);
  const simuW = doc.getTextWidth('SimulReno');

  // Orange dot ●
  doc.setFillColor(...C.orangeDot);
  doc.circle(MARGIN + simuW + 3.5, 12.5, 1.5, 'F');

  // "ESTIMATION BUDGÉTAIRE DE TRAVAUX" right-aligned, muted white, 9pt
  font(doc, roboto, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.mutedWhite);
  doc.text('ESTIMATION BUDGÉTAIRE DE TRAVAUX', PAGE_W - MARGIN, 14, { align: 'right' });

  // Estimation number + date, below header, right-aligned, #7C6FA0, 8pt
  font(doc, roboto, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.subText);
  doc.text(
    `Estimation n° ${devisNum}  ·  ${new Date().toLocaleDateString('fr-FR')}`,
    PAGE_W - MARGIN, HEADER_H + 6, { align: 'right' },
  );

  let y = HEADER_H + 14;

  // ── INFO BLOCK ─────────────────────────────────────────────────────────────
  const loc = data.description?.location;
  const address = [loc?.address, loc?.postcode, loc?.city].filter(Boolean).join(', ') || 'Adresse non renseignée';
  const propType = PROPERTY_TYPE_LABELS[data.description?.propertyType ?? ''] ?? 'Non renseigné';
  const area = data.description?.area ? `${data.description.area} m²` : 'Non renseigné';

  const infoH = 23; // C1 — reduced vertical padding to ~5mm
  doc.setFillColor(...C.purpleBg);
  doc.roundedRect(MARGIN, y, CONTENT_W, infoH, 2, 2, 'F');

  font(doc, roboto, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.violet);
  doc.text('INFORMATIONS DU BIEN', MARGIN + 5, y + 6);

  font(doc, roboto, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.darkText);
  doc.text(address, MARGIN + 5, y + 13);            // adresse + CP + ville
  doc.text(`${propType}  ·  ${area}`, MARGIN + 5, y + 19); // type · surface, même ligne

  // Zone géographique alignée à droite si disponible
  if (deptCode) {
    font(doc, roboto, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.subText); // #7C6FA0
    doc.text(`Zone géographique : département ${deptCode}`, PAGE_W - MARGIN - 5, y + 19, { align: 'right' });
  }

  y += infoH + 5; // 5mm gap

  // ── CATEGORIES ─────────────────────────────────────────────────────────────
  for (const category of result.categories) {
    // Reserve space for header + col headers + at least 2 rows
    y = ensureSpace(doc, y, CAT_H + ROW_H + ROW_H * 2);

    // Category title bar
    doc.setFillColor(...C.violet);
    doc.rect(MARGIN, y, CONTENT_W, CAT_H, 'F');
    font(doc, roboto, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.white);
    doc.text(category.categoryTitle.toUpperCase(), MARGIN + 4, y + 5);
    y += CAT_H;

    // Column headers
    doc.setFillColor(...C.headBg);
    doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
    font(doc, roboto, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.purple);
    COL_HEADERS.forEach((header, i) => {
      const align = COL_ALIGN[i];
      const tx =
        align === 'right' ? colX(i) + COL_W[i] - 2 :
        align === 'center' ? colX(i) + COL_W[i] / 2 :
        colX(i) + 2;
      doc.text(header, tx, y + 4.3, { align });
    });
    y += ROW_H;

    // Items
    category.items.forEach((item, idx) => {
      // Check for sub-description detail
      const itemMeta = estimationDataService.getItem(item.category, item.itemKey);
      const det = itemMeta?.details;
      const detParts: string[] = [];
      if (det?.materials?.length) detParts.push(`Matériaux : ${det.materials.slice(0, 3).join(', ')}`);
      if (det?.workIncluded?.length) detParts.push(`Travaux : ${det.workIncluded.slice(0, 2).join(', ')}`);
      if (det?.note) detParts.push(det.note);
      const subText = detParts.join('  |  ');
      const hasDetail = subText.length > 0;

      const totalH = ROW_H + (hasDetail ? 4.5 : 0);
      y = ensureSpace(doc, y, totalH);

      // Alternating row background
      if (idx % 2 === 1) {
        doc.setFillColor(...C.rowAlt);
        doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
      }

      font(doc, roboto, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...C.darkText);

      const maxDesig = COL_W[0] - 4;
      doc.text(truncate(doc, item.name, maxDesig), colX(0) + 2, y + 4.3);
      doc.text(String(item.quantity), colX(1) + COL_W[1] / 2, y + 4.3, { align: 'center' });
      doc.text(item.unit || '', colX(2) + COL_W[2] / 2, y + 4.3, { align: 'center' });
      doc.text(formatPriceForPDF(item.unitPrice), colX(3) + COL_W[3] - 2, y + 4.3, { align: 'right' });
      doc.text(`${item.tvaRate}%`, colX(4) + COL_W[4] / 2, y + 4.3, { align: 'center' });
      doc.text(formatPriceForPDF(item.totalPrice), colX(5) + COL_W[5] - 2, y + 4.3, { align: 'right' });
      doc.text(formatPriceForPDF(item.totalTTC), colX(6) + COL_W[6] - 2, y + 4.3, { align: 'right' });

      // Row separator
      doc.setDrawColor(...C.purpleBorder);
      doc.setLineWidth(0.1);
      doc.line(MARGIN, y + ROW_H, MARGIN + CONTENT_W, y + ROW_H);
      y += ROW_H;

      // Sub-description
      if (hasDetail) {
        y = ensureSpace(doc, y, 4.5);
        doc.setFillColor(...C.subDetailBg);
        doc.rect(MARGIN, y, CONTENT_W, 4.5, 'F');
        font(doc, roboto, 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...C.subText);
        doc.setFont(roboto ? 'Roboto' : 'helvetica', 'italic');
        doc.text(
          doc.splitTextToSize(subText, CONTENT_W - 4)[0],
          MARGIN + 2, y + 3.3,
        );
        y += 4.5;
      }
    });

    // Discount row
    if (category.discountPercentage && category.discountAmount) {
      y = ensureSpace(doc, y, ROW_H);
      doc.setFillColor(...C.discountBg);
      doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
      font(doc, roboto, 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...C.discountRed);
      doc.text(`Décote volume ${category.discountPercentage}%`, MARGIN + 2, y + 4.3);
      doc.text(`− ${formatPriceForPDF(category.discountAmount)}`, colX(5) + COL_W[5] - 2, y + 4.3, { align: 'right' });
      y += ROW_H;
    }

    // Category total row
    y = ensureSpace(doc, y, ROW_H);
    doc.setFillColor(...C.purplePale);
    doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
    font(doc, roboto, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.purple);
    doc.text(`Total ${category.categoryTitle} (HT)`, MARGIN + 2, y + 4.3);
    doc.text(formatPriceForPDF(category.subtotal), PAGE_W - MARGIN - 2, y + 4.3, { align: 'right' });
    y += ROW_H + 6; // C2 — 6mm max between sections
  }

  // ── TVA BLOCK ──────────────────────────────────────────────────────────────
  const tvaH = 10 + result.tvaBreakdown.length * 7 + 4;
  y = ensureSpace(doc, y, tvaH + 6);

  doc.setDrawColor(...C.purpleBorder);
  doc.setLineWidth(0.3);
  doc.setFillColor(...C.white);
  doc.roundedRect(MARGIN, y, CONTENT_W, tvaH, 2, 2, 'FD');

  font(doc, roboto, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.violet);
  doc.text('DÉTAIL DE LA TVA', MARGIN + 5, y + 6.5);

  let ty = y + 12;
  result.tvaBreakdown.forEach(tvaItem => {
    font(doc, roboto, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.darkText);
    doc.text(`TVA ${tvaItem.rate}%`, MARGIN + 5, ty);
    doc.text(`Base HT : ${formatPriceForPDF(tvaItem.baseHT)}`, MARGIN + 35, ty);
    doc.text(`TVA : ${formatPriceForPDF(tvaItem.tvaAmount)}`, MARGIN + 90, ty);
    font(doc, roboto, 'bold');
    doc.text(`TTC : ${formatPriceForPDF(tvaItem.totalTTC)}`, PAGE_W - MARGIN - 5, ty, { align: 'right' });
    ty += 7;
  });

  y += tvaH + 5; // 5mm gap

  // ── FINANCIAL SUMMARY ──────────────────────────────────────────────────────
  // C3 — split summary: pale top (#EDE9FE/#4C1D95) + violet bottom (#3D1A78/white)
  const htTvaH  = 23; // HT + TVA zone
  const ttcH    = 14; // TTC zone
  const summaryH = htTvaH + ttcH;
  y = ensureSpace(doc, y, summaryH + 4);

  // Top section — #EDE9FE, rounded top corners
  doc.setFillColor(...C.purplePale);
  doc.roundedRect(MARGIN, y, CONTENT_W, htTvaH, 3, 3, 'F');
  doc.rect(MARGIN, y + htTvaH - 3, CONTENT_W, 3, 'F'); // square off bottom edge

  // Bottom section — #3D1A78, rounded bottom corners
  doc.setFillColor(...C.violet);
  doc.roundedRect(MARGIN, y + htTvaH, CONTENT_W, ttcH, 3, 3, 'F');
  doc.rect(MARGIN, y + htTvaH, CONTENT_W, 3, 'F'); // square off top edge

  // HT + TVA text (#4C1D95)
  font(doc, roboto, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.purple);
  doc.text('Montant total HT', MARGIN + 8, y + 9);
  doc.text(formatPriceForPDF(result.totalHT), PAGE_W - MARGIN - 8, y + 9, { align: 'right' });
  doc.text('Total TVA', MARGIN + 8, y + 17);
  doc.text(formatPriceForPDF(result.tva), PAGE_W - MARGIN - 8, y + 17, { align: 'right' });

  // TTC text (white)
  font(doc, roboto, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.white);
  doc.text('TOTAL TTC', MARGIN + 8, y + htTvaH + 10);
  doc.text(formatPriceForPDF(result.totalTTC), PAGE_W - MARGIN - 8, y + htTvaH + 10, { align: 'right' });

  y += summaryH + 5; // 5mm before legal mention

  // ── LEGAL MENTION — C4: 8mm space already added above ────────────────────
  const legalText =
    "Les prix sont basés sur les tarifs du marché à début 2026 et sont volontairement surévalués d'environ 10 %. " +
    "Ce document est une estimation préliminaire et ne constitue pas un devis contractuel.";
  y = ensureSpace(doc, y, 12);
  font(doc, roboto, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.gray); // #9CA3AF
  doc.text(doc.splitTextToSize(legalText, CONTENT_W), MARGIN, y);

  // ── FOOTERS ON ALL PAGES ───────────────────────────────────────────────────
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawFooter(doc, p, total, roboto);
  }

  doc.save(`estimation-detaillee-${new Date().toISOString().split('T')[0]}.pdf`);
}

// ─── Quick PDF ─────────────────────────────────────────────────────────────────

async function generateQuickPDF(estimate: EstimateWithId): Promise<void> {
  const doc = new jsPDF();
  const roboto = await loadRoboto(doc);

  // ── C1 HEADER — same gradient as detailed ──────────────────────────────────
  drawGradientHeader(doc);

  font(doc, roboto, 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text('SimulReno', MARGIN, 14);
  const simuW = doc.getTextWidth('SimulReno');

  doc.setFillColor(...C.orangeDot);
  doc.circle(MARGIN + simuW + 3.5, 12.5, 1.5, 'F');

  font(doc, roboto, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.mutedWhite);
  doc.text('ESTIMATION RAPIDE', PAGE_W - MARGIN, 14, { align: 'right' });

  font(doc, roboto, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.subText);
  doc.text(new Date().toLocaleDateString('fr-FR'), PAGE_W - MARGIN, HEADER_H + 6, { align: 'right' });

  let y = HEADER_H + 14;

  // ── C2 INFO BLOCK — same style as detailed ─────────────────────────────────
  const loc = estimate.location;
  const address = loc
    ? [loc.address, loc.postcode, loc.city].filter(Boolean).join(', ')
    : 'Adresse non renseignée';
  const propType = PROPERTY_TYPE_LABELS[estimate.propertyType ?? ''] ?? 'Non renseigné';
  const area = estimate.area ? `${estimate.area} m²` : 'Non renseigné';

  const infoH = 23;
  doc.setFillColor(...C.purpleBg);
  doc.roundedRect(MARGIN, y, CONTENT_W, infoH, 2, 2, 'F');

  font(doc, roboto, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.violet);
  doc.text('INFORMATIONS DU BIEN', MARGIN + 5, y + 6);

  font(doc, roboto, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.darkText);
  doc.text(address, MARGIN + 5, y + 13);
  doc.text(`${propType}  ·  ${area}`, MARGIN + 5, y + 19);

  y += infoH + 6;

  // ── C3 SCENARIO TABLE — manual drawing, no autotable ──────────────────────
  if (estimate.priceRange) {
    const { min, max, average } = estimate.priceRange;
    const mid = average || Math.round((min + max) / 2);

    // Section label
    font(doc, roboto, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.violet);
    doc.text('ESTIMATION BUDGÉTAIRE', MARGIN, y);
    y += 6;

    const TABLE_HEAD_H = 7;
    const TABLE_ROW_H  = 8;

    // Header row — violet background, white text
    doc.setFillColor(...C.violet);
    doc.rect(MARGIN, y, CONTENT_W, TABLE_HEAD_H, 'F');
    font(doc, roboto, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.white);
    doc.text('Scénario', MARGIN + 4, y + 5);
    doc.text('Montant estimé', PAGE_W - MARGIN - 4, y + 5, { align: 'right' });
    y += TABLE_HEAD_H;

    // C4 — use formatPriceForPDF + setCharSpace(0) to fix kerning
    const rows: { label: string; amount: number }[] = [
      { label: 'Travaux économiques (min)', amount: min },
      { label: 'Travaux standards',        amount: mid },
      { label: 'Travaux premium (max)',     amount: max },
    ];

    rows.forEach((row, idx) => {
      // Alternating background
      if (idx % 2 === 1) {
        doc.setFillColor(...C.rowAlt);
        doc.rect(MARGIN, y, CONTENT_W, TABLE_ROW_H, 'F');
      }
      // Row separator
      doc.setDrawColor(...C.purpleBorder);
      doc.setLineWidth(0.1);
      doc.line(MARGIN, y + TABLE_ROW_H, MARGIN + CONTENT_W, y + TABLE_ROW_H);

      // Label
      font(doc, roboto, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...C.darkText);
      doc.text(row.label, MARGIN + 4, y + 5.5);

      // Amount — bold, right-aligned, charSpace reset for correct kerning
      font(doc, roboto, 'bold');
      doc.setCharSpace(0);
      doc.text(formatPriceForPDF(row.amount), PAGE_W - MARGIN - 4, y + 5.5, { align: 'right' });

      y += TABLE_ROW_H;
    });

    // ── C5 SUMMARY BAR — violet, "ESTIMATION MOYENNE" + amount ─────────────
    y += 2;
    const summaryBarH = 12;
    doc.setFillColor(...C.violet);
    doc.roundedRect(MARGIN, y, CONTENT_W, summaryBarH, 2, 2, 'F');
    font(doc, roboto, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...C.white);
    doc.setCharSpace(0);
    doc.text('ESTIMATION MOYENNE', MARGIN + 6, y + 8.5);
    doc.text(formatPriceForPDF(mid), PAGE_W - MARGIN - 6, y + 8.5, { align: 'right' });
    y += summaryBarH + 5;
  }

  // ── C6 LEGAL MENTION ───────────────────────────────────────────────────────
  const legalText =
    "Les prix sont basés sur les tarifs du marché à début 2026 et sont volontairement surévalués d'environ 10 %. " +
    "Ce document est une estimation préliminaire et ne constitue pas un devis contractuel.";
  y = ensureSpace(doc, y, 12);
  font(doc, roboto, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.gray);
  doc.text(doc.splitTextToSize(legalText, CONTENT_W), MARGIN, y);

  // ── C6 FOOTER — same as detailed ───────────────────────────────────────────
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawFooter(doc, p, total, roboto);
  }

  doc.save(`estimation-rapide-${new Date().toISOString().split('T')[0]}.pdf`);
}

// ─── Main dispatcher ───────────────────────────────────────────────────────────

export async function generateEstimatePDF(estimate: EstimateWithId): Promise<void> {
  const preparedData = migrationService.prepareForDisplay(estimate);
  const estimateType = (preparedData.type || estimate.type || 'quick') as string;

  if (estimateType === 'detailed') {
    await generateDetailedPDF(preparedData as DetailedEstimationData);
  } else {
    await generateQuickPDF(estimate);
  }
}
